import { useCallback } from 'react'
import * as api from 'api/entityApi'
import { EmptyState, ErrorState, InlineLoader } from 'components/common/Loader'
import { useAsyncData } from 'hooks/useAsyncData'
import type { ChatLogItem } from 'types/entity'

/**
 * Port of `getWhatsappChatLog` / `getEnquiryWhatsappChatLog`: resolve the
 * conversation user id from IMSService, then load that conversation.
 *
 * The legacy `InitWaLog` plugin also opened an MQTT socket for live messages;
 * this renders the history and refreshes on demand instead.
 */
export function WaChatLogPanel({
  entityId,
  entityName,
}: {
  entityId: string
  entityName: 'lead' | 'enquiry'
}) {
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<ChatLogItem[]> => {
      const convUserId = await api.fetchWaConversationUserId(entityName, entityId, signal)
      if (!convUserId) return []
      return api.fetchWaChatLog(entityName, entityId, convUserId, signal)
    },
    [entityId, entityName],
  )

  const { data, loading, error, reload } = useAsyncData(fetcher, [entityId, entityName])

  if (loading) return <InlineLoader />
  if (error) return <ErrorState message={error} onRetry={reload} />
  if (!data || data.length === 0) return <EmptyState message="No WhatsApp conversation for this record." />

  return (
    <>
      <div className="mb-1 text-right">
        <button type="button" className="btn btn-icon" onClick={reload} title="Refresh">
          ⟳
        </button>
      </div>
      <div className="chat-log">
        {data.map((chat) => (
          <div key={chat.id} className={`chat-bubble ${chat.direction}`}>
            {chat.message}
            <span className="meta">
              {chat.sender} · {chat.sentOn}
            </span>
          </div>
        ))}
      </div>
    </>
  )
}
